import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import { useOrganizationStore } from "@/stores/organization.js";
import { useCustomAuthRoleGroupsStore } from "@/stores/role.js";
import { useUnitStore } from "@/stores/unit";
import { useCountsStore } from "@/stores/counts";
import { useDepartmentStore } from "@/stores/department";
import { useWebSearchTopicStore } from "@/stores/web_search_topic";
import { useTeamStore } from "@/stores/team";
import { useTicketPriority } from "@/stores/ticketPriority";
import { useMessengerStore } from "@/stores/messenger";
import { useWfbChatStore } from "@/stores/wfbChat";
import { useThemeStore } from "@/stores/theme";


const MAX_RETRIES = 3;



export function useAppInitializer() {
  const auth = useAuthStore();
  const user = useUserStore();
  const organization = useOrganizationStore();
  const dpt = useDepartmentStore();
  const topic = useWebSearchTopicStore();
  const team = useTeamStore();
  const unit = useUnitStore();
  const counts = useCountsStore();
  const roles = useCustomAuthRoleGroupsStore();
  const messenger = useMessengerStore();
  const wfbChatStore = useWfbChatStore();
  const theme = useThemeStore();
  const ticketPriorityStore = useTicketPriority();

  const isLoading = ref(false);

  const _loadUserProfile = async () => {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        await user.fetchMyProfile()
        break
      } catch(err) {
        if (attempt === MAX_RETRIES) throw err
        console.error("Failed to fetch user profile. Retrying...")
      }
    }
  }

  const _loadOrganization = async () => {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        await organization.fetchMyOrganization()
        break
      } catch(err) {
        if (attempt === MAX_RETRIES) throw err
        console.error("Failed to fetch user organization. Retrying...")
      }
    }
  }

  const _hydrateStores = async () => {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        await Promise.all([
          counts.fetchInteractionCounts(),
          counts.fetchTrainingCounts(),
        ])

        // non-end user store hydration
        if (user.permCodes.includes("view-inbox-page")) {
          await Promise.all([
            organization.fetchAll(),
            dpt.fetchAll(),
            topic.fetchAll(),
            team.fetchAll(),
            user.fetchAllUsers(),
            unit.fetchAll(),
            roles.fetchAll(),
            ticketPriorityStore.load(user.authUserOrganizationId),
          ])
        }
        break
      } catch(err) {
        if (attempt === MAX_RETRIES) throw err
        console.error("Failed to hydrate stores. Retrying...")
      }
    }
  }

  const initializeData = async () => {
    isLoading.value = true
    try {
      if(!(await auth.isLoggedIn())) return

      await _loadUserProfile()
      await _loadOrganization()

      // update the organization theme colour state when its available
      theme.updatePrimaryColor(
        organization.myOrganization?.highlight_color || "#00A8E8"
      );

      // Must be after fetchMyOrganization so to check agentic migration org setting.
      wfbChatStore.initialize().then((result) => {
        console.debug("wfbChatStore initialized?", result);
        if (result !== false) return;
        messenger.rtcToAcServer();
      });

      await _hydrateStores()
    } finally {
      isLoading.value = false
    }
  }

  return { isLoading, initializeData }
}
